import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  Title,
  CopyButton,
  ActionIcon,
  Tooltip,
  rem,
  Breadcrumbs,
  Anchor,
  Divider,
  Button,
} from "@mantine/core";
import {
  IconCopy,
  IconCheck,
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons-react";
import { PAGES } from "./docsIndex";
import { useScrollIntoView } from "@mantine/hooks";
import DocsHeader from "./DocsHeader";
import DocsFooter from "./docsFooter";
import "react-json-view-lite/dist/index.css";
import { TableOfContents } from "./articlesHelpers/TableOfContents";
import DocsServiceNotice from "./DocsServiceNotice";

const DocsContainer: React.FC = () => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pageData, setPageData] = useState<
    (typeof PAGES)[keyof typeof PAGES] | null
  >(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Ref to store each article's HTML element
  const articleRefs = useRef<{ [slug: string]: HTMLDivElement | null }>({});

  // Single useScrollIntoView hook with offset
  const { scrollIntoView } = useScrollIntoView<HTMLDivElement>({
    offset: 80,
  });

  useEffect(() => {
    console.log("[DocsContainer] Initial mount effect running");

    // Prevent browser scroll restoration from interfering
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const rootDiv = document.getElementById("root");
    const pageData = JSON.parse(rootDiv?.dataset.hydrate || "{}");

    console.log("[DocsContainer] Parsed page data:", pageData);
    const page = Object.values(PAGES).find(
      (p) => p.slug === pageData.page_slug
    );
    if (page) {
      console.log("[DocsContainer] Found page:", page.slug);
      setPageData(page);
    } else {
      console.log(
        "[DocsContainer] No page found for slug:",
        pageData.page_slug
      );
    }
    setIsLoggedIn(pageData.is_logged_in);
    setHasLoaded(true);
  }, []);

  // Separate effect to handle scrolling after articles are rendered
  useEffect(() => {
    console.log(
      "[DocsContainer] Scroll effect running - hasLoaded:",
      hasLoaded,
      "pageData:",
      !!pageData
    );
    if (!hasLoaded || !pageData) {
      console.log("[DocsContainer] Skipping scroll - not ready yet");
      return;
    }

    // Handle both full path and legacy hash-based URLs
    const pathname = window.location.pathname;
    const hash = window.location.hash.slice(1);
    const pathSegments = pathname.split("/").filter(Boolean);
    let articleSlug: string | null = null;

    console.log(
      "[DocsContainer] Pathname:",
      pathname,
      "Hash:",
      hash,
      "Segments:",
      pathSegments
    );

    // Check for full-path URL (e.g., /docs/balances-and-capacities/inbound-capacity)
    if (pathSegments.length > 2 && pathSegments[0] === "docs") {
      articleSlug = pathSegments[pathSegments.length - 1];
      console.log(
        "[DocsContainer] Detected full-path article slug:",
        articleSlug
      );
    }
    // Check for legacy hash-based URL (e.g., /docs/balances-and-capacities#inbound-capacity)
    else if (hash && pathSegments.length >= 2 && pathSegments[0] === "docs") {
      articleSlug = hash;
      console.log(
        "[DocsContainer] Detected hash-based article slug:",
        articleSlug
      );
      // Optionally redirect to full-path URL for consistency
      const pageSlug = pathSegments[1];
      window.history.replaceState(null, "", `/docs/${pageSlug}/${articleSlug}`);
    }

    console.log("[DocsContainer] Article slug to scroll to:", articleSlug);
    console.log(
      "[DocsContainer] Available article refs:",
      Object.keys(articleRefs.current)
    );
    console.log(
      "[DocsContainer] Target ref exists:",
      !!articleRefs.current[articleSlug || ""]
    );

    // Scroll to the specific article if a slug is found
    if (articleSlug && articleRefs.current[articleSlug]) {
      console.log(
        "[DocsContainer] Setting up scroll timeout for:",
        articleSlug
      );
      // Use requestAnimationFrame to ensure all rendering/layout is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const element = articleRefs.current[articleSlug];
          console.log("[DocsContainer] In RAF - element exists:", !!element);
          if (element) {
            console.log("[DocsContainer] Scrolling to element:", articleSlug);
            const elementTop =
              element.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementTop - 80;

            console.log(
              "[DocsContainer] Element top:",
              elementTop,
              "Offset position:",
              offsetPosition
            );

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
            console.log("[DocsContainer] Scroll completed");
          } else {
            console.log("[DocsContainer] Element disappeared before RAF");
          }
        });
      });
    } else {
      if (articleSlug) {
        console.log("[DocsContainer] Article slug exists but ref not found");
      } else {
        console.log("[DocsContainer] No article slug in URL");
      }
    }
  }, [hasLoaded, pageData]);

  useEffect(() => {
    if (pageData) {
      const updateTitle = () => {
        const pathname = window.location.pathname;
        const pathSegments = pathname.split("/").filter(Boolean);
        let articleSlug: string | null = null;
        if (pathSegments.length > 2 && pathSegments[0] === "docs") {
          articleSlug = pathSegments[pathSegments.length - 1];
        }

        if (articleSlug) {
          const targetArticle = pageData.ARTICLES.find(
            (article) => article.slug === articleSlug
          );
          if (targetArticle) {
            const newTitle = `${targetArticle.title} - ${pageData.title} - Rizful Documentation`;
            document.title = newTitle;
            const ogTitleMeta = document.querySelector(
              'meta[property="og:title"]'
            );
            if (ogTitleMeta) {
              ogTitleMeta.setAttribute("content", newTitle);
            }
          }
        }
      };

      window.addEventListener("popstate", updateTitle);
      updateTitle();

      return () => {
        window.removeEventListener("popstate", updateTitle);
      };
    }
  }, [pageData]);

  const baseDomain = window.location.origin;

  // Breadcrumb items
  const breadcrumbsItems = [
    { title: "Rizful Node Docs", href: "/docs" },
    pageData && { title: pageData.title, href: `/docs/${pageData.slug}` },
  ]
    .filter(Boolean)
    .map((item, index) => (
      <Anchor href={item.href} key={index} underline="hover">
        {item.title}
      </Anchor>
    ));

  // Determine previous and next pages
  const pageKeys = Object.keys(PAGES);
  const currentIndex = pageData
    ? pageKeys.findIndex((key) => PAGES[key].slug === pageData.slug)
    : -1;
  const previousPage =
    currentIndex > 0 ? PAGES[pageKeys[currentIndex - 1]] : null;
  const nextPage =
    currentIndex < pageKeys.length - 1
      ? PAGES[pageKeys[currentIndex + 1]]
      : null;

  const links =
    pageData?.ARTICLES.map((article) => ({
      label: article.title,
      link: `/docs/${pageData.slug}/${article.slug}`, // Full path for navigation
      order: 1,
    })) || [];
  console.log("links", links);

  return (
    <>
      {hasLoaded && pageData && (
        <>
          <DocsHeader is_logged_in={isLoggedIn} />
          <Stack gap="lg" style={{ padding: "var(--mantine-spacing-lg)" }}>
            <DocsServiceNotice />
            <Breadcrumbs
              separator="→"
              mb="md"
              styles={{
                root: {
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                },
                breadcrumb: {
                  whiteSpace: "normal",
                },
              }}
            >
              {breadcrumbsItems}
            </Breadcrumbs>

            <Title order={1}>{pageData.title}</Title>
            {pageData.ARTICLES.length > 1 && <TableOfContents links={links} />}

            {pageData.ARTICLES.map((article) => (
              <Stack
                key={article.slug}
                ref={(el) => {
                  console.log(
                    "[DocsContainer] Setting ref for article:",
                    article.slug,
                    "element:",
                    !!el
                  );
                  articleRefs.current[article.slug] = el;
                }}
                id={article.slug}
                gap="sm"
              >
                <Group gap="xs" justify="flex-start" wrap="nowrap">
                  {article.icon}
                  <Title order={3}>{article.title}</Title>
                  <CopyButton
                    value={`${baseDomain}/docs/${pageData.slug}/${article.slug}`}
                    timeout={2000}
                  >
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "Copied" : "Copy"}
                        withArrow
                        position="right"
                      >
                        <ActionIcon
                          color={copied ? "teal" : "gray"}
                          variant="subtle"
                          onClick={copy}
                        >
                          {copied ? (
                            <IconCheck style={{ width: rem(16) }} />
                          ) : (
                            <IconCopy style={{ width: rem(16) }} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
                {article.content}
                <Divider />
              </Stack>
            ))}
            <Group justify="space-between" mt="lg">
              {previousPage && (
                <Button
                  component="a"
                  href={`/docs/${previousPage.slug}`}
                  variant="light"
                  leftSection={<IconArrowLeft />}
                >
                  {previousPage.title}
                </Button>
              )}
              {nextPage && (
                <Button
                  component="a"
                  href={`/docs/${nextPage.slug}`}
                  variant="light"
                  rightSection={<IconArrowRight />}
                >
                  {nextPage.title}
                </Button>
              )}
            </Group>
          </Stack>
          <DocsFooter />
        </>
      )}
    </>
  );
};

export default DocsContainer;
